// root '../../../';

// dependencies
import { Child } from 'pym.js';
import * as d3 from 'd3';
import Zion from './utils/Zion';

// polyfills
// import fetch from 'unfetch';
import './utils/polyfills'; // (includes Array.findIndex && Array.find)

// utils
import { stylePremium } from './utils/helpers';

// styles
import './styles/graphic.scss';

/* develblock:start */
import meta from './data/meta.json';

const pkg = require('../package.json');

if (process.env.NODE_ENV === 'development') {
  console.clear();
}

d3.timeFormatDefaultLocale({
  dateTime: '%A, %e de %B de %Y, %X',
  date: '%d/%m/%Y',
  time: '%H:%M:%S',
  periods: ['a. m.', 'p. m.'],
  days: [
    'domingo',
    'lunes',
    'martes',
    'miércoles',
    'jueves',
    'viernes',
    'sábado',
  ],
  shortDays: ['dom.', 'lun.', 'mar.', 'mié.', 'jue.', 'vie.', 'sáb.'],
  months: [
    'enero',
    'febrero',
    'marzo',
    'abril',
    'mayo',
    'junio',
    'julio',
    'agosto',
    'septiembre',
    'octubre',
    'noviembre',
    'diciembre',
  ],
  shortMonths: [
    'ene.',
    'feb.',
    'mar.',
    'abr.',
    'may.',
    'jun.',
    'jul.',
    'ago.',
    'sep.',
    'oct.',
    'nov.',
    'dic.',
  ],
});

function formatUsd(value, minFrac = 2, maxFrac = 2) {
  const n = Number(value);
  if (Number.isNaN(n)) return '';
  const s = n.toLocaleString('es-ES', {
    minimumFractionDigits: minFrac,
    maximumFractionDigits: maxFrac,
  });
  return `US$ ${s}`;
}

function formatUsdAxis(value) {
  const n = Number(value);
  if (Number.isNaN(n)) return '';
  const rounded = Math.abs(n - Math.round(n)) < 1e-6
    ? Math.round(n)
    : n;
  const s = rounded.toLocaleString('es-ES', {
    minimumFractionDigits: Number.isInteger(rounded) ? 0 : 2,
    maximumFractionDigits: 2,
  });
  return `US$ ${s}`;
}

function formatEsPctPoints(points) {
  return `${Math.abs(points).toLocaleString('es-ES', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} %`;
}

function formatEsPctFromRatio(ratio) {
  if (ratio == null || Number.isNaN(ratio)) return '';
  const pct = ratio * 100;
  return `${pct.toLocaleString('es-ES', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} %`;
}

let chartData;
let rawData;
let currentData;
let lastData;

const margins = {
  top: 20,
  right: 145,
  bottom: 25,
  left: 76,
};

let minValue;
let maxValue;

let minDomain;
let maxDomain;

let brentStats = null;

// eslint-disable-next-line  no-use-before-define
window.pymChild = new Child({ id: pkg.name });

window.onload = () => {
  // necessary evil for right now

  if (process.env.NODE_ENV === 'development') {
    Object.keys(meta).forEach((id) => {
      const node = document.querySelector(`.${id}`);

      if (node) {
        node.innerHTML = meta[id];
      }
    });
  }

  if (brentStats) {
    updateDekAndFootnote();
  }

  const { pymChild } = window;
  pymChild.sendHeight();

  // If embedded, send Zion page view event
  if (window.pymChild.parentUrl) {
    const zion = new Zion(pkg.name, pymChild.parentUrl.split('?')[0]);
  }
};

const CNNPolitics = false;
const CNNBusiness = false;

const BRENT_LIVE_URL = 'https://production.dataviz.cnn.io/charting/instruments/latest/list/Commodity/BZC00';

stylePremium(CNNPolitics, CNNBusiness);

let livePrice = null;

function getETDateRange() {
  const now = new Date();
  const etNow = new Date(now.toLocaleString('en-US', { timeZone: 'America/New_York' }));

  const endDate = etNow.toISOString().split('T')[0];
  const startDate = new Date(etNow);
  startDate.setFullYear(startDate.getFullYear() - 1);

  return {
    start: startDate.toISOString().split('T')[0],
    end: endDate,
  };
}

function formatLongDate(isoDate) {
  const d = d3.timeParse('%Y-%m-%d')(isoDate);
  if (!d) return isoDate;
  const month = d3.timeFormat('%B')(d);
  return `${d.getDate()} de ${month} de ${d.getFullYear()}`;
}

function formatLiveTimestampParts(isoTimestamp) {
  if (!isoTimestamp) return { dateLine: '', timeLine: '' };
  const d = new Date(isoTimestamp);
  const opts = { timeZone: 'America/New_York' };
  const dateLine = d.toLocaleDateString('es-ES', {
    day: 'numeric',
    month: 'long',
    ...opts,
  });
  let timeStr = d.toLocaleTimeString('es-ES', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    ...opts,
  });
  timeStr = timeStr.replace(/\ba\.\s*m\./gi, 'a.m.').replace(/\bp\.\s*m\./gi, 'p.m.');
  const timeLine = `${timeStr} (Miami)`;
  return { dateLine, timeLine };
}

function updateDekAndFootnote() {
  if (!brentStats) return;

  const s = brentStats;

  const displayPrice = livePrice ? livePrice.currentPrice : s.latestValue;
  const displayChange = livePrice ? livePrice.currentPrice - s.latestValue : s.dailyChange;
  const displayPctChange = livePrice ? ((livePrice.currentPrice - s.latestValue) / s.latestValue) * 100 : s.dailyPctChange;

  const required = [displayPrice, displayPctChange];
  if (required.some(v => Number.isNaN(v))) return;

  const direction = displayPctChange >= 0 ? 'un aumento de' : 'una bajada de';
  const pctStr = formatEsPctPoints(displayPctChange);
  const priceStr = formatUsd(displayPrice);
  const pctChangeColor = displayPctChange >= 0 ? '#53A796' : '#c52622';

  const dekHtml = `El precio más reciente del Brent, referente global del petróleo, es de <strong>${priceStr}</strong>, ${direction} <strong style="color: ${pctChangeColor};">${pctStr}</strong> respecto al precio de cierre anterior.`;

  const dekPlain = `El precio más reciente del Brent, referente global del petróleo, es de ${priceStr}, ${direction} ${pctStr} respecto al precio de cierre anterior.`;

  const dekEl = document.querySelector('.dek');
  if (dekEl) {
    dekEl.innerHTML = dekHtml;
    dekEl.setAttribute('aria-label', dekPlain);
  }

  const footEl = document.querySelector('.footnote');
  if (footEl) {
    footEl.textContent = 'Nota: los valores históricos corresponden al precio de liquidación de los futuros del crudo Brent negociados en la Bolsa Mercantil de Nueva York (NYMEX), en dólares estadounidenses por barril. El último precio se actualiza a lo largo de la jornada.';
  }

  if (window.pymChild) {
    window.pymChild.sendHeight();
  }
}

function applySettlePayload(settles) {
  const parseDay = d3.timeParse('%Y-%m-%d');

  rawData = settles
    .map(d => ({
      date: parseDay(d.event_date),
      close: +d.current_price,
    }))
    .filter(d => d.date && !Number.isNaN(d.close))
    .sort((a, b) => +a.date - +b.date);

  if (!rawData.length) return;

  const latest = rawData[rawData.length - 1];
  const previous = rawData[rawData.length - 2];

  currentData = {
    date: latest.date,
    close: latest.close,
    prevClose: previous.close,
    change: latest.close - previous.close,
    percentChange: (latest.close - previous.close) / previous.close,
  };

  brentStats = {
    latestDate: settles[settles.length - 1].event_date,
    latestValue: latest.close,
    previousClose: previous.close,
    dailyChange: currentData.change,
    dailyPctChange: currentData.percentChange * 100,
  };

  const seriesEnd = d3.max(rawData, d => d.date);
  const windowStart = d3.timeYear.offset(seriesEnd, -1);
  chartData = rawData.filter(d => d.date >= windowStart);

  minDomain = d3.min(chartData, d => d.date);
  maxDomain = d3.max([d3.max(chartData, d => d.date), currentData.date]);

  const dataMin = d3.min(chartData, d => d.close);
  const dataMax = d3.max(chartData, d => d.close);
  const span = dataMax - dataMin || 1;
  const pad = span * 0.2;
  minValue = dataMin - pad;
  maxValue = dataMax + pad;

  lastData = chartData[chartData.length - 1];
  updateDekAndFootnote();
}

function fetchSettleData() {
  const { start, end } = getETDateRange();
  const url = `https://production.dataviz.cnn.io/charting/instruments/BZC00/Commodity/${start}/${end}/DAILY/false?response_type=default`;
  return fetch(`${url}?t=${Date.now()}`)
    .then(response => response.json())
    .then((data) => {
      const settles = data.filter(d => d.trade_condition === '' && d.market_volume === 0);
      applySettlePayload(settles);
      draw();
    })
    .catch((error) => {
      console.error('Error fetching settle data:', error);
    });
}

function fetchLivePrice() {
  const cacheBust = `?t=${Date.now()}`;
  return fetch(BRENT_LIVE_URL + cacheBust)
    .then(response => response.json())
    .then((result) => {
      if (result && result.data && result.data[0]) {
        const live = result.data[0];
        const currentPrice = +live.current_price;
        livePrice = {
          currentPrice,
          lastUpdated: live.last_updated,
        };
        updateDekAndFootnote();
        draw();
      }
    })
    .catch((error) => {
      console.error('Error fetching live Brent price:', error);
    });
}

fetchSettleData().then(() => {
  fetchLivePrice();
  draw();
  window.onresize = draw;

  setInterval(fetchLivePrice, 30 * 1000);
});

function draw() {
  if (!chartData || !chartData.length || minDomain == null || maxDomain == null) {
    return;
  }

  const container = d3.select('#graphic');
  container.html('');

  const initialWidth = container.node().clientWidth;
  const isMobile = initialWidth <= 500;
  const height = 400;

  const marginLeft = isMobile ? 58 : margins.left;
  const chartHeight = height - margins.top - margins.bottom;
  const chartWidth = initialWidth - marginLeft - margins.right;

  const yTicks = isMobile ? 6 : 6;

  const xScale = d3.scaleTime()
    .domain([minDomain, maxDomain])
    .range([10, chartWidth]);

  const yScale = d3.scaleLinear()
    .domain([minValue, maxValue])
    .range([chartHeight, 0]);

  const yTickValues = yScale.ticks(yTicks);
  const yTickWithCurrency = yTickValues.length
    ? yTickValues[yTickValues.length - 1]
    : null;

  const xAxis = d3.axisBottom()
    .scale(xScale)
    .ticks(isMobile ? 2 : 6)
    .tickValues(isMobile ? [minDomain, maxDomain] : null)
    .tickSize(6)
    .tickFormat((d, i, nodes) => {
      if (isMobile) {
        return d3.timeFormat('%b \'%y')(d);
      }
      return d3.timeFormat('%b %Y')(d);
    });

  const yAxis = d3.axisLeft()
    .scale(yScale)
    .ticks(yTicks)
    .tickFormat((d) => {
      if (yTickWithCurrency != null && Math.abs(d - yTickWithCurrency) < 1e-9) {
        return formatUsdAxis(d);
      }
      return Number(d).toLocaleString('es-ES', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
    })
    .tickSize(-chartWidth);

  const path = d3.line()
    .curve(d3.curveMonotoneX)
    .x(d => xScale(d.date))
    .y(d => yScale(d.close));

  const svg = container.append('svg')
    .attr('width', initialWidth)
    .attr('height', height);

  const g = svg.append('g')
    .attr('transform', `translate(${[marginLeft, margins.top]})`);

  g.append('g')
    .attr('class', 'grid x')
    .attr('transform', `translate(${[0, chartHeight]})`)
    .call(xAxis);

  g.append('g')
    .attr('class', 'grid y')
    .call(yAxis);

  const yLabelX = -(marginLeft - 6);
  g.select('.grid.y').selectAll('.tick text')
    .attr('text-anchor', 'start')
    .attr('dx', 0)
    .attr('x', yLabelX);

  g.append('line')
    .attr('class', 'x-baseline')
    .attr('x1', 0)
    .attr('x2', chartWidth)
    .attr('y1', chartHeight)
    .attr('y2', chartHeight)
    .attr('stroke', '#b1b1b1')
    .attr('stroke-width', 1);

  const dayKey = d => d3.timeFormat('%Y-%m-%d')(d);
  const combinedData = chartData.slice();

  if (currentData) {
    const last = combinedData[combinedData.length - 1];
    if (last && dayKey(last.date) === dayKey(currentData.date)) {
      combinedData[combinedData.length - 1] = { date: currentData.date, close: currentData.close };
    } else {
      combinedData.push({ date: currentData.date, close: currentData.close });
    }
  }

  if (livePrice && currentData) {
    const liveDate = new Date(currentData.date);
    liveDate.setHours(23, 59, 59);
    const liveDataPoint = { date: liveDate, close: livePrice.currentPrice };
    combinedData.push(liveDataPoint);
  }

  combinedData.sort((a, b) => +a.date - +b.date);

  d3.select('.grid.y .domain').remove();

  const eventDate = d3.timeParse('%Y-%m-%d')('2026-02-28');
  if (eventDate && eventDate >= minDomain && eventDate <= maxDomain) {
    const eventX = xScale(eventDate);

    const eventGroup = g.append('g')
      .attr('class', 'event-marker');

    eventGroup.append('line')
      .attr('class', 'event-line')
      .attr('x1', eventX)
      .attr('x2', eventX)
      .attr('y1', 0)
      .attr('y2', chartHeight)
      .attr('stroke', '#8C8C8C')
      .attr('stroke-width', 1)
      .attr('stroke-dasharray', '3 3');

    const spaceLeft = eventX;
    const spaceRight = chartWidth - eventX;
    const anchor = spaceRight > spaceLeft ? 'start' : 'end';
    const textX = anchor === 'start' ? eventX + 6 : eventX - 6;

    const eventText = eventGroup.append('text')
      .attr('class', 'event-annotation')
      .attr('x', textX)
      .attr('y', 40)
      .attr('text-anchor', anchor);

    eventText.append('tspan')
      .attr('x', textX)
      .attr('class', 'event-date')
      .text('28 feb. 2026:');

    if (isMobile) {
      eventText.append('tspan')
        .attr('x', textX)
        .attr('dy', '1.2em')
        .attr('class', 'event-label')
        .text('Comienza la guerra');
      eventText.append('tspan')
        .attr('x', textX)
        .attr('dy', '1.2em')
        .attr('class', 'event-label')
        .text('con Irán');
    } else {
      eventText.append('tspan')
        .attr('x', textX)
        .attr('dy', '1.2em')
        .attr('class', 'event-label')
        .text('Comienza la guerra con Irán');
    }
  }

  const line = g.append('g')
    .attr('class', 'line');

  line.append('path')
    .datum(combinedData)
    .attr('stroke', '#f18851')
    .attr('stroke-width', '1.5')
    .attr('fill', 'none')
    .attr('d', path);

  if (currentData) {
    const displayPrice = livePrice ? livePrice.currentPrice : currentData.close;
    const displayChange = livePrice ? livePrice.currentPrice - currentData.close : currentData.change;
    const displayPctChange = livePrice ? (livePrice.currentPrice - currentData.close) / currentData.close : currentData.percentChange;

    const changeColor = displayChange >= 0 ? '#53A796' : '#c52622';

    const cx = xScale(currentData.date);
    const cy = yScale(displayPrice);

    const currentPoint = g.append('g')
      .attr('class', 'current-point')
      .attr('transform', `translate(${cx},${cy})`);

    currentPoint.append('circle')
      .attr('r', '4')
      .attr('fill', changeColor)
      .attr('stroke', 'white')
      .attr('stroke-width', '2');

    const annotation = g.append('g')
      .attr('class', 'annotation')
      .attr('transform', `translate(${cx},${cy})`);

    annotation.append('text')
      .attr('dx', '10px')
      .attr('class', 'annotation-price')
      .text(`Último: ${formatUsd(displayPrice)}`);

    const changePrefix = displayChange >= 0 ? '+' : '−';
    const changeAbs = Math.abs(displayChange);
    const changeMoney = `${changePrefix}US$ ${changeAbs.toLocaleString('es-ES', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

    annotation.append('text')
      .attr('dx', '10px')
      .attr('class', 'annotation-change')
      .attr('fill', changeColor)
      .text(`${changeMoney} (${formatEsPctFromRatio(displayPctChange)})`)
      .attr('dy', '1.2em');

    if (livePrice && livePrice.lastUpdated) {
      const { dateLine, timeLine } = formatLiveTimestampParts(livePrice.lastUpdated);
      annotation.append('text')
        .attr('dx', '10px')
        .attr('class', 'annotation-time')
        .attr('dy', '2.4em')
        .text(dateLine);
      annotation.append('text')
        .attr('dx', '10px')
        .attr('class', 'annotation-time')
        .attr('dy', '3.6em')
        .text(timeLine);
    } else {
      const settleDateText = brentStats ? formatLongDate(brentStats.latestDate) : '';
      annotation.append('text')
        .attr('dx', '10px')
        .attr('class', 'annotation-time')
        .attr('dy', '2.4em')
        .text(settleDateText);
    }
  }

  window.pymChild.sendHeight();
}
